import { useDispatch, useSelector } from "react-redux";
import { Column } from "../../../../core/components/Column"
import { AppDispatch, RootState } from "../../../../core/store/store";
import { UserView } from "../../../../core/components/UserView";
import { CheckVerify } from "../../../../core/icons/CheckVerify";
import { Report } from "../../../../core/icons/Flag";
import { ReportUser } from "../../../../core/components/ReportUser";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Row } from "../../../../core/components/Row";
import { LoadingProfilePictuture } from "../components/LoadingProfilePicture";
import { NoProfiles } from "../components/NoProfiles"
import { AsideNotification } from "../components/AsideNotification";
import useSwipe from "../hook/useSwipe";
import { onGetMatchSearch } from "../store/MatchThunk";
import { GetMatchSearchParams } from "../../domain/usecases/getMatchSearch";
import { Match, MatchesSearch } from "../../domain/entities/userUnjabbed";
import {
    setCurrentPage,
    setCurrenUserIndex,
    setHasExecuted,
    setHasMore,
    setMatchesLoading,
    setResetSearch,
    setCurrentRadio,
	bumpSearchVersion,
	updateMatchesSearch,
} from "../store/MatchSlice";
import useMatch from "../hook/useMatch";
import { clearAllCachedImagesExcept, precacheImages, sortPhotos } from "../../../../core/constants/constants";
import { ButtonLikeDislike } from "../components/ButtonLikeDisLike";
import { PurchaseMessage } from "../../../../core/components/PurchaseMessage";
import { motion, AnimatePresence } from "framer-motion";
import { NavigationButton } from "../components/NavigationButton";
import { getMatchLocationParams, getSelectedDiscoveryCountry, hasActiveManualDiscoveryOverride } from "../../../../core/utilities/locationStorage";
import { formatDistanceText, formatPublicProfileDistanceText } from "../../../../core/utilities/distance";
import {
    relationshipPrioritiesDataSource,
    SharedPriority,
} from "../../../relationship-priorities/data/RelationshipPrioritiesDataSource";
import { SharedPrioritiesPanel } from "../../../relationship-priorities/presentation/SharedPrioritiesPanel";
import { MeaningfulIntroductionModal } from "../../../relationship-priorities/presentation/MeaningfulIntroductionModal";
import {
	openPreviousProfileReview,
	rememberViewedProfile,
	readPersistedReinstatedSecondLookUserIds,
	readStoredDiscoverySession,
	restartDiscoveryRadius,
	SECOND_LOOK_EMPTY_MESSAGE,
	swipeLeftInPreviousProfileReview,
	viewedExclusionsKeepingReinstatedProfilesEligible,
	writePersistedReinstatedSecondLookUserIds,
	writePersistedFartherHighWater,
	writeStoredDiscoverySession,
} from "../utilities/discoverySession";
import { saveSecondLookDecision } from "../../data/datasource/SecondLookDecisionDataSource";

const LIMIT = 20;
const START_POSITION = 5;
const MAX_FARTHER_RADIUS = 2000;
const FARTHER_RADIUS_STEP = 400;

export const nextFartherDiscoveryRadius = (currentRadius: number): number => {
    const safeRadius = Math.min(
        Math.max(currentRadius || FARTHER_RADIUS_STEP, 1),
        MAX_FARTHER_RADIUS,
    );
    if (safeRadius >= MAX_FARTHER_RADIUS) return MAX_FARTHER_RADIUS;
    return Math.min(
        (Math.floor(safeRadius / FARTHER_RADIUS_STEP) + 1) * FARTHER_RADIUS_STEP,
        MAX_FARTHER_RADIUS,
    );
};

export const continueFartherBatchRadius = (
	currentRadius: number,
	hasMoreEligibleWithinRadius: boolean,
): number => hasMoreEligibleWithinRadius
	? currentRadius
	: nextFartherDiscoveryRadius(currentRadius);

export const MatchScreen = () => {
    const [isReport, setIsReport] = useState<boolean>(false);
    const { user, getProfileConfig } = useSelector((state: RootState) => state.onboarding);
	const storedDiscoverySession = useRef(
		readStoredDiscoverySession(user?.id),
	).current;
	const storedDiscoverySessionUserId = useRef(user?.id);
    const profilePictureUrl = user?.profile_pictures?.find(picture => picture.is_profile)?.url || "";
    const dispatch: AppDispatch = useDispatch();
    const {
        matchesSearch,
        currentUserIndex,
        currentRadio,
        currentPage,
        hasMore,
        isContinue,
        hasExecuted,
        matchesLoading,
        matchesSearchCount,
        searchVersion,
    } = useSelector((state: RootState) => state.match);
	const [revisitQueueOnEntry, setRevisitQueueOnEntry] = useState(Boolean(matchesSearch?.second_look_snapshot));
	const searchOwnerMounted = useRef(true);
	useEffect(() => {
		searchOwnerMounted.current = true;
		return () => {
			searchOwnerMounted.current = false;
			dispatch(bumpSearchVersion());
			dispatch(setMatchesLoading(false));
		};
	}, [dispatch]);
	const latestSearchVersion = useRef(searchVersion);
	latestSearchVersion.current = searchVersion;
    const { handleLike, handleUpdateMatchesSearch } = useMatch();
    const [rippleLike, setRippleLike] = useState(false);
    const [rippleDislike, setRippleDislike] = useState(false);
    const [idReport, setIdReport] = useState("");
    const [reportedImageUrl, setReportedImageUrl] = useState("");
	const [secondLook, setSecondLook] = useState(false);
	const [checkedForNewProfiles, setCheckedForNewProfiles] = useState(false);
	const [checkExcludedUserIds, setCheckExcludedUserIds] = useState<string[]>([]);
	const [minimumDistanceExclusive, setMinimumDistanceExclusive] = useState<number>();
	const [hasMoreEligibleWithinRadius, setHasMoreEligibleWithinRadius] = useState(false);
	const [viewedProfiles, setViewedProfiles] = useState<Match[]>(
		storedDiscoverySession.viewedProfiles,
	);
	const [viewedProfileIds, setViewedProfileIds] = useState<string[]>(
		storedDiscoverySession.viewedProfileIds,
	);
	const [viewedFromContinueFartherIds, setViewedFromContinueFartherIds] = useState<string[]>(
		storedDiscoverySession.viewedFromContinueFartherIds ?? [],
	);
	const [farthestViewedFartherDistance, setFarthestViewedFartherDistance] = useState<number | undefined>(
		storedDiscoverySession.farthestViewedFartherDistance,
	);
	const [reinstatedSecondLookUserIds, setReinstatedSecondLookUserIds] = useState<string[]>(
		readPersistedReinstatedSecondLookUserIds(user?.id),
	);
	const [pendingDecisionCount, setPendingDecisionCount] = useState(0);
	const [secondLookSnapshot, setSecondLookSnapshot] = useState<string>();
	const secondLookReturnedAny = useRef(false);
	const pendingConfirmedDecisionUserIds = useRef<Set<string>>(new Set());
	const [secondLookRequestSucceeded, setSecondLookRequestSucceeded] = useState(false);
	const [secondLookStarting, setSecondLookStarting] = useState(false);
	const [choiceMessage, setChoiceMessage] = useState<string>();
	const [searchFailed, setSearchFailed] = useState(false);
	const [revisitCompleted, setRevisitCompleted] = useState(false);
	const [continueFartherMode, setContinueFartherMode] = useState(false);
	const [restartMaximumDistance, setRestartMaximumDistance] = useState<number>();
	const [reviewingPrevious, setReviewingPrevious] = useState(false);
	const [previousProfileIndex, setPreviousProfileIndex] = useState(0);
	const [prioritySummaries, setPrioritySummaries] = useState<Record<string, SharedPriority[]>>({});
	const [todayCandidateId, setTodayCandidateId] = useState<string | null>(null);
	const [meaningfulOpen, setMeaningfulOpen] = useState(false);
	const [direction, setDirection] = useState<number>(1);
		const [, setIsAnimating] = useState<boolean>(false);
    const permissionState = localStorage.getItem("LOCATION_PERMISSION_STATE");
	const savedDiscoveryRadius =
		getProfileConfig?.search_filters.max_distance || currentRadio || FARTHER_RADIUS_STEP;
	const activeDiscoveryRadius = currentRadio || savedDiscoveryRadius;
	const continueAtCurrentRadius = minimumDistanceExclusive !== undefined && hasMoreEligibleWithinRadius;
	const nextFartherRadius = continueFartherBatchRadius(
		activeDiscoveryRadius,
		continueAtCurrentRadius,
	);
	const canContinueFarther = continueAtCurrentRadius || nextFartherRadius > activeDiscoveryRadius;
	const currentMatch = matchesSearch?.matches[currentUserIndex];
	const inactiveQueueNeedsReset = revisitQueueOnEntry || (!secondLook &&
		currentRadio > savedDiscoveryRadius &&
		minimumDistanceExclusive === undefined &&
		restartMaximumDistance === undefined);

	useEffect(() => {
		if (!currentMatch || reviewingPrevious || inactiveQueueNeedsReset) return;
		setViewedProfiles((profiles) => rememberViewedProfile(profiles, currentMatch));
		setViewedProfileIds((profileIds) =>
			profileIds.includes(currentMatch.id)
				? profileIds
				: [...profileIds, currentMatch.id],
		);
		setViewedFromContinueFartherIds((profileIds) =>
			continueFartherMode
				? Array.from(new Set([...profileIds, currentMatch.id]))
				: profileIds.filter((profileId) => profileId !== currentMatch.id),
		);
		if (continueFartherMode && Number(currentMatch.distance) > savedDiscoveryRadius) {
			setFarthestViewedFartherDistance((distance) => {
				const nextDistance = Math.max(
					distance ?? savedDiscoveryRadius,
					Number(currentMatch.distance),
				);
				writePersistedFartherHighWater(user?.id, nextDistance);
				return nextDistance;
			});
		}
	}, [continueFartherMode, currentMatch, reviewingPrevious, savedDiscoveryRadius, user?.id, inactiveQueueNeedsReset]);
    const priorityCandidateIds = useMemo(
        () => matchesSearch?.matches.slice(0, 50).map((match) => match.id) ?? [],
        [matchesSearch?.matches],
    );
    const priorityCandidateSignature = priorityCandidateIds.join(",");

    useEffect(() => {
        const ids = priorityCandidateSignature.split(",").filter(Boolean);
        if (!ids.length) {
            setPrioritySummaries({});
            setTodayCandidateId(null);
            return;
        }
        let active = true;
        relationshipPrioritiesDataSource.summaries(ids).then((result) => {
            if (!active) return;
            setPrioritySummaries(result.summaries || {});
            setTodayCandidateId(result.today_candidate_id || null);
        }).catch(() => {
            // This optional feature must never interrupt normal Discovery.
        });
        return () => {
            active = false;
        };
    }, [priorityCandidateSignature]);

	const refreshDiscoveryFromStart = useCallback((checkedForNewProfiles = false, excludedUserIds: string[] = []) => {
		setRevisitQueueOnEntry(false);
		setRevisitCompleted(false);
		setCheckedForNewProfiles(checkedForNewProfiles);
		setCheckExcludedUserIds(excludedUserIds);
		setMinimumDistanceExclusive(undefined);
		setHasMoreEligibleWithinRadius(false);
		setContinueFartherMode(false);
		setRestartMaximumDistance(undefined);
		setSecondLook(false);
		setSecondLookSnapshot(undefined);
		secondLookReturnedAny.current = false;
		setSecondLookRequestSucceeded(false);
		setSecondLookStarting(false);
		setChoiceMessage(undefined);
		setReviewingPrevious(false);
        setDirection(1);
		dispatch(setCurrentRadio(savedDiscoveryRadius));
        dispatch(setResetSearch());
    }, [dispatch, savedDiscoveryRadius]);
	const previousSavedDiscoveryRadius = useRef(
		storedDiscoverySession.savedDiscoveryRadius ?? savedDiscoveryRadius,
	);
	const savedDistanceNeedsReconciliation =
		previousSavedDiscoveryRadius.current !== savedDiscoveryRadius;
	// Wider/Revisit queues can survive in Redux after this screen unmounts, while
	// its explicit local consent/batch state does not. Return to saved filters
	// rather than relabel that queue or continue it with the country flag lost.
	const searchScopeNeedsReconciliation = savedDistanceNeedsReconciliation || inactiveQueueNeedsReset;

	useEffect(() => {
		if (!user?.id || storedDiscoverySessionUserId.current === user.id) return;
		const nextStoredSession = readStoredDiscoverySession(user.id);
		storedDiscoverySessionUserId.current = user.id;
		previousSavedDiscoveryRadius.current =
			nextStoredSession.savedDiscoveryRadius ?? savedDiscoveryRadius;
		setViewedProfiles(nextStoredSession.viewedProfiles);
		setViewedProfileIds(nextStoredSession.viewedProfileIds);
		setViewedFromContinueFartherIds(
			nextStoredSession.viewedFromContinueFartherIds ?? [],
		);
		setFarthestViewedFartherDistance(nextStoredSession.farthestViewedFartherDistance);
		setReinstatedSecondLookUserIds(
			readPersistedReinstatedSecondLookUserIds(user.id),
		);
	}, [savedDiscoveryRadius, user?.id]);

	useEffect(() => {
		if (!searchScopeNeedsReconciliation) return;
		previousSavedDiscoveryRadius.current = savedDiscoveryRadius;
		refreshDiscoveryFromStart(
			false,
			viewedExclusionsKeepingReinstatedProfilesEligible(
				viewedProfileIds,
				reinstatedSecondLookUserIds,
			),
		);
	}, [
		refreshDiscoveryFromStart,
		savedDiscoveryRadius,
		searchScopeNeedsReconciliation,
		reinstatedSecondLookUserIds,
		viewedProfileIds,
	]);

	useEffect(() => {
		writeStoredDiscoverySession(user?.id, {
			viewedProfiles,
			viewedProfileIds,
			viewedFromContinueFartherIds,
			farthestViewedFartherDistance,
			savedDiscoveryRadius,
		});
	}, [
		farthestViewedFartherDistance,
		savedDiscoveryRadius,
		user?.id,
		viewedProfileIds,
		viewedFromContinueFartherIds,
		viewedProfiles,
	]);

	useEffect(() => {
		writePersistedReinstatedSecondLookUserIds(
			user?.id,
			reinstatedSecondLookUserIds,
		);
	}, [reinstatedSecondLookUserIds, user?.id]);

    const variants = {
		enter: (dir: number) => ({ x: dir * 24, opacity: 0 }),
		center: { x: 0, opacity: 1 },
		exit: (dir: number) => ({ x: dir * -24, opacity: 0 }),
	};

    useEffect(() => {
        const locationParams = getMatchLocationParams();
        const hasStoredDiscoveryLocation = Boolean(
            locationParams.user_lat && locationParams.user_lng
        );
        if (
			searchScopeNeedsReconciliation ||
			!user ||
			!hasMore ||
			currentRadio === 0 ||
			!hasStoredDiscoveryLocation
		) return;
        const params: GetMatchSearchParams = {
            params: {
                ...locationParams,
                page: currentPage.toString(),
                limit: LIMIT.toString(),
					radio: currentRadio.toString(),
					second_look: secondLook,
					include_other_countries: !secondLook &&
						(minimumDistanceExclusive !== undefined || restartMaximumDistance !== undefined),
					second_look_snapshot: secondLook ? secondLookSnapshot : undefined,
					reviewed_user_ids: secondLook
						? matchesSearch?.matches.map((match) => match.id).join(",") || undefined
						: undefined,
            },
            clientSearchVersion: searchVersion,
			clientExcludedUserIds: checkExcludedUserIds.length
				? checkExcludedUserIds
				: undefined,
			clientMinimumDistanceExclusive: minimumDistanceExclusive,
			clientMaximumDistanceInclusive: restartMaximumDistance,
			clientMaximumMatches: minimumDistanceExclusive !== undefined ? LIMIT : undefined,
			clientExpandFartherIfEmpty: minimumDistanceExclusive !== undefined,
        };

		const fetchMatches = async () => {
            dispatch(setMatchesLoading(true));
			const result = await dispatch(onGetMatchSearch(params));
			if (!searchOwnerMounted.current || params.clientSearchVersion !== latestSearchVersion.current) return;
			if (!result?.payload) {
				setSearchFailed(true);
				if (secondLook) {
					setSecondLook(false);
					setSecondLookSnapshot(undefined);
					setSecondLookRequestSucceeded(false);
					setChoiceMessage("Unable to revisit crossed profiles. Please try again.");
				} else {
					setChoiceMessage("Unable to load profiles. Please try again. Your search settings and previous choices have not changed.");
				}
				setSecondLookStarting(false);
				dispatch(setMatchesLoading(false));
				return;
			}
			setSearchFailed(false);
			const {
				matches,
				sourceMatchesCount,
				clientHasMoreEligibleWithinRadius,
				second_look_snapshot: returnedSecondLookSnapshot,
			} = result?.payload as MatchesSearch;
			if (minimumDistanceExclusive !== undefined) {
				setHasMoreEligibleWithinRadius(Boolean(clientHasMoreEligibleWithinRadius));
			}
			const sourceCount = sourceMatchesCount ?? matches.length;
			if (secondLook) {
				setSecondLookRequestSucceeded(true);
				setSecondLookSnapshot(returnedSecondLookSnapshot);
				if (matches.length > 0) {
					secondLookReturnedAny.current = true;
					setChoiceMessage(undefined);
				} else if (!secondLookReturnedAny.current) {
					setChoiceMessage(SECOND_LOOK_EMPTY_MESSAGE);
				}
				setReinstatedSecondLookUserIds((userIds) =>
					Array.from(new Set([...userIds, ...matches.map((match) => match.id)])),
				);
			}
			setSecondLookStarting(false);
			if (matches.length > 0) {
				setCheckedForNewProfiles(false);
			}
            const lastUsers = matchesSearch?.matches.slice(-5);
            if (lastUsers) {
                const urlsToKeep = lastUsers.flatMap(user =>
                    user.profile_pictures?.slice(0, 3).map(pic =>
                        typeof pic === "string" ? pic : pic.url
                    ) || []
                );
                clearAllCachedImagesExcept(urlsToKeep);
            }
            matches.forEach(match => {
                const imageUrls = match.profile_pictures?.slice(0, 3).map(pic =>
                    typeof pic === "string" ? pic : pic.url
                ) || [];
                precacheImages(imageUrls);
            });
            
			if (sourceCount === LIMIT) {
                dispatch(setCurrentPage(currentPage + 1));
                dispatch(setHasExecuted(false));
                dispatch(setHasMore(false));
			} else if (sourceCount < LIMIT) {
                dispatch(setHasMore(false));
            }
            dispatch(setMatchesLoading(false));
        };
        fetchMatches();
    }, [
        currentPage,
        currentRadio,
        dispatch,
        hasMore,
        permissionState,
        searchVersion,
		secondLook,
		secondLookSnapshot,
		checkExcludedUserIds,
		minimumDistanceExclusive,
		restartMaximumDistance,
		searchScopeNeedsReconciliation,
        user,
    ]);

    useEffect(() => {
        if (!matchesSearch || hasExecuted) return;
        if (currentUserIndex + 1 >= matchesSearch.matches.length - START_POSITION && matchesSearchCount === LIMIT) {
            dispatch(setHasMore(true));
            dispatch(setHasExecuted(true));
        }
    }, [currentUserIndex, matchesSearch, hasExecuted]);

    useEffect(() => {
        const handleVisible = () => {
            if (document.visibilityState === "visible") {
				refreshDiscoveryFromStart();
            }
        };
        const handleNewNearbyMembers = () => {
			refreshDiscoveryFromStart();
        };

        document.addEventListener("visibilitychange", handleVisible);
        window.addEventListener("focus", handleNewNearbyMembers);
        window.addEventListener("unjabbed:new-nearby-members", handleNewNearbyMembers);

        return () => {
            document.removeEventListener("visibilitychange", handleVisible);
            window.removeEventListener("focus", handleNewNearbyMembers);
            window.removeEventListener("unjabbed:new-nearby-members", handleNewNearbyMembers);
        };
	}, [refreshDiscoveryFromStart]);

    const handleContinue = () => {
        refreshDiscoveryFromStart();
    };

	const handleSecondLook = () => {
		setRevisitCompleted(false);
		if (pendingDecisionCount > 0 || secondLookStarting) {
			setChoiceMessage("Please wait while your Cross is saved.");
			return;
		}
		setCheckedForNewProfiles(false);
		setCheckExcludedUserIds([]);
		setMinimumDistanceExclusive(undefined);
		setHasMoreEligibleWithinRadius(false);
		setContinueFartherMode(false);
		setRestartMaximumDistance(undefined);
		setSecondLook(true);
		setSecondLookSnapshot(undefined);
		secondLookReturnedAny.current = false;
		setSecondLookRequestSucceeded(false);
		setSecondLookStarting(true);
		setChoiceMessage(undefined);
		setReviewingPrevious(false);
		setDirection(1);
		dispatch(setCurrentRadio(savedDiscoveryRadius));
		dispatch(setResetSearch());
	};

	const handleContinueFarther = () => {
		setRevisitCompleted(false);
		if (!canContinueFarther) return;
		setCheckedForNewProfiles(false);
		setCheckExcludedUserIds(
			viewedExclusionsKeepingReinstatedProfilesEligible(
				viewedProfileIds,
				reinstatedSecondLookUserIds,
			),
		);
		setMinimumDistanceExclusive(savedDiscoveryRadius);
		setHasMoreEligibleWithinRadius(false);
		setContinueFartherMode(true);
		setRestartMaximumDistance(undefined);
		setSecondLook(false);
		setSecondLookSnapshot(undefined);
		secondLookReturnedAny.current = false;
		setSecondLookRequestSucceeded(false);
		setSecondLookStarting(false);
		setChoiceMessage(undefined);
		setReviewingPrevious(false);
		setDirection(1);
		dispatch(setCurrentRadio(nextFartherRadius));
		dispatch(setResetSearch());
	};

	const handleCheckNew = () => {
		if (
			pendingDecisionCount > 0 ||
			pendingConfirmedDecisionUserIds.current.size > 0
		) {
			setChoiceMessage("Please wait while your Cross is saved.");
			return;
		}
		refreshDiscoveryFromStart(
			true,
			viewedExclusionsKeepingReinstatedProfilesEligible(
				viewedProfileIds,
				reinstatedSecondLookUserIds,
			),
		);
	};

	const handleStartAgain = () => {
		setRevisitCompleted(false);
		if (pendingDecisionCount > 0) {
			setChoiceMessage("Please wait while your Cross is saved.");
			return;
		}
		const restartRadius = restartDiscoveryRadius(
			savedDiscoveryRadius,
			farthestViewedFartherDistance,
		);
		setCheckedForNewProfiles(false);
		setCheckExcludedUserIds([]);
		setMinimumDistanceExclusive(undefined);
		setHasMoreEligibleWithinRadius(false);
		setContinueFartherMode(false);
		setRestartMaximumDistance(
			(farthestViewedFartherDistance ?? savedDiscoveryRadius) > savedDiscoveryRadius
				? farthestViewedFartherDistance
				: undefined,
		);
		setSecondLook(false);
		setSecondLookSnapshot(undefined);
		secondLookReturnedAny.current = false;
		setSecondLookRequestSucceeded(false);
		setSecondLookStarting(false);
		setChoiceMessage(undefined);
		setReviewingPrevious(false);
		setDirection(1);
		dispatch(setCurrentRadio(restartRadius));
		dispatch(setResetSearch());
	};

	const handleBackToPrevious = () => {
		if (!viewedProfiles.length) return;
		const review = openPreviousProfileReview(viewedProfiles.length);
		setPreviousProfileIndex(review.previousProfileIndex);
		setReviewingPrevious(review.reviewingPrevious);
		setDirection(review.direction);
	};

    const handleNextUser = () => {
		if (reviewingPrevious) {
			const review = swipeLeftInPreviousProfileReview(
				{ reviewingPrevious, previousProfileIndex, direction },
				viewedProfiles.length,
			);
			setReviewingPrevious(review.reviewingPrevious);
			setPreviousProfileIndex(review.previousProfileIndex);
			setDirection(review.direction);
			return;
		}
        if (matchesSearch && currentUserIndex <= matchesSearch.matches.length - 1) {
		    setDirection(1);
            dispatch(setCurrenUserIndex(currentUserIndex + 1));
        }
    };

    const handlePrevUser = () => {
		if (reviewingPrevious) {
			if (previousProfileIndex > 0) {
				setDirection(-1);
				setPreviousProfileIndex(previousProfileIndex - 1);
			}
			return;
		}
        if (currentUserIndex > 0) {
		    setDirection(-1);
            dispatch(setCurrenUserIndex(currentUserIndex - 1));
        }
    };

	const EndCard = (direction: number ) => {
        return (
            <motion.div
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4, ease: "easeInOut" }}
            >
				{matchesLoading || secondLookStarting || searchScopeNeedsReconciliation ?
                    <Column
                        mainAxisAlignment="center"
                        crossAxisAlignment="center"
                        className="flex-shrink-0 w-full h-full min-md:min-h-[91svh] max-md:max-h-[91svh] max-md:h-[91svh]"
                    >
                        <LoadingProfilePictuture
                            profile={profilePictureUrl}
                        />
                    </Column>
                    :
                    <Column
                        mainAxisAlignment="center"
                        crossAxisAlignment="center"
                        className="flex-shrink-0 w-full cursor-grab md:h-full select-none min-md:min-h-[91svh] max-md:max-h-[91svh] max-md:h-[91svh]"
                    >
                        <NoProfiles
                            handleContinue={handleContinue}
							handleSecondLook={handleSecondLook}
                            handleContinueFarther={handleContinueFarther}
                            handleCheckNew={handleCheckNew}
							handleStartAgain={handleStartAgain}
                            handleBack={handleBackToPrevious}
                            canGoBack={viewedProfiles.length > 0}
                            canContinueFarther={canContinueFarther}
							choicesEnabled={pendingDecisionCount === 0}
							isContinue={isContinue}
							checkedForNewProfiles={checkedForNewProfiles}
							choiceMessage={choiceMessage}
							searchFailed={searchFailed}
							moreAvailable={hasMoreEligibleWithinRadius}
							revisitResult={revisitCompleted || choiceMessage === SECOND_LOOK_EMPTY_MESSAGE || choiceMessage?.startsWith("Unable to revisit")}
							restartIncludesOtherCountries={(farthestViewedFartherDistance ?? savedDiscoveryRadius) > savedDiscoveryRadius}
							handleRetry={() => {
								setSearchFailed(false);
								if (choiceMessage?.startsWith("Unable to revisit")) handleSecondLook();
								else { setChoiceMessage(undefined); dispatch(setResetSearch()); }
							}}
							searchCountry={hasActiveManualDiscoveryOverride() ? getSelectedDiscoveryCountry() : undefined}
							searchAges={getProfileConfig?.search_filters.age_range}
							searchRadius={activeDiscoveryRadius}
							searchDistanceLabel={formatDistanceText(activeDiscoveryRadius, {country: user?.country, city: user?.city}).replace(/ away$/, "")}
							widerSearch={minimumDistanceExclusive !== undefined || restartMaximumDistance !== undefined}
							permissionState={permissionState}
                            isCrossCountrySearch={false}
                        />
                    </Column>
                }
            </motion.div>
        );
    }
    const displayMatch = reviewingPrevious
        ? viewedProfiles[previousProfileIndex]
        : currentMatch;
	const isCustomComponent = !reviewingPrevious && currentUserIndex === matchesSearch?.matches.length;
	useEffect(() => {
		if (
			!secondLook ||
			!secondLookRequestSucceeded ||
			matchesLoading ||
			hasMore ||
			!matchesSearch ||
			currentUserIndex !== matchesSearch.matches.length
		) return;

		setSecondLook(false);
		setSecondLookRequestSucceeded(false);
		setSecondLookSnapshot(undefined);
		setRevisitCompleted(true);
		setChoiceMessage((message) =>
			secondLookReturnedAny.current ? undefined : message,
		);
		// An already-empty result needs no store update. Replacing it again can
		// synchronously re-enter this effect before its local state settles.
		if (matchesSearch.matches.length > 0) {
			dispatch(updateMatchesSearch({ matches: [], page: 1 }));
		}
	}, [
		currentUserIndex,
		hasMore,
		matchesLoading,
		matchesSearch,
		dispatch,
		secondLook,
		secondLookRequestSucceeded,
	]);
    const handleIsReport = (imageUrl?: string) => {
        if (!matchesSearch || reviewingPrevious || !displayMatch) return;
        setIsReport((prev) => !prev);
        setIdReport(displayMatch.id);
        setReportedImageUrl(imageUrl || "");
    };
    
    const handleSuccessReport = () => {
        const currentUserId = idReport;
        setIdReport("");
        setReportedImageUrl("");
        handleUpdateMatchesSearch(currentUserId);
    };

	const handleLikeMatch = async (type: "like" | "dislike") => {
		if (!matchesSearch || !displayMatch) return;
		setDirection(1);
		const currentUserId = displayMatch.id;
		const wasSecondLookDecision = secondLook && !reviewingPrevious;
		const wasReinstatedSecondLookProfile =
			reinstatedSecondLookUserIds.includes(currentUserId);
		const requiresConfirmedDecision =
			reviewingPrevious ||
			type === "dislike" ||
			wasSecondLookDecision ||
			wasReinstatedSecondLookProfile;
		if (
			requiresConfirmedDecision &&
			pendingConfirmedDecisionUserIds.current.has(currentUserId)
		) {
			return;
		}
		const decisionMustSettle = requiresConfirmedDecision;
		if (decisionMustSettle) {
			setPendingDecisionCount((count) => count + 1);
		}
        const setRipple = type === "like" ? setRippleLike : setRippleDislike;
		if (requiresConfirmedDecision) {
			pendingConfirmedDecisionUserIds.current.add(currentUserId);
		}
		const decisionSucceeded = requiresConfirmedDecision
			? saveSecondLookDecision(
				currentUserId,
				type === "like" ? "liked" : "rejected",
				reviewingPrevious
					? "discovery"
					: wasSecondLookDecision
					? "second_look"
					: continueFartherMode
						? "continue_farther"
						: "discovery",
			).then(() => true, () => false)
			: handleLike(currentUserId, type);
        setRipple(true);
        await new Promise(resolve => setTimeout(resolve, 300));
        setRipple(false);
		const succeeded = await decisionSucceeded;
		if (succeeded || !requiresConfirmedDecision) {
			setViewedProfiles((profiles) =>
				profiles.filter((profile) => profile.id !== currentUserId),
			);
			if (reviewingPrevious) {
				if (previousProfileIndex <= 0 || viewedProfiles.length <= 1) {
					setReviewingPrevious(false);
				} else {
					setPreviousProfileIndex(previousProfileIndex - 1);
				}
			} else {
				handleUpdateMatchesSearch(currentUserId);
			}
		}
		if (
			succeeded &&
			(wasSecondLookDecision || wasReinstatedSecondLookProfile)
		) {
			setReinstatedSecondLookUserIds((userIds) =>
				userIds.filter((userId) => userId !== currentUserId),
			);
		}
		if (requiresConfirmedDecision) {
			pendingConfirmedDecisionUserIds.current.delete(currentUserId);
		}
		if (decisionMustSettle) {
			setPendingDecisionCount((count) => Math.max(count - 1, 0));
			setChoiceMessage((message) =>
				message === "Please wait while your Cross is saved."
					? undefined
					: message,
			);
		}
    };

    const { handleTouchStart, handleTouchMove, handleTouchEnd, handleMouseDown, handleMouseMove, handleMouseUp } = useSwipe(
        handleNextUser,
        handlePrevUser,    
    );

    const normalizeLocationValue = (value?: string): string => {
        const cleaned = (value || "").trim();
        if (!cleaned) return "";
        const lowered = cleaned.toLowerCase();
        if (cleaned === "-" || cleaned === "--" || lowered === "null" || lowered === "undefined") {
            return "";
        }
        return cleaned;
    };

    const getDisplayLocation = (city?: string, country?: string): { location: string; country: string } => {
        const normalizedCountry = normalizeLocationValue(country);
        if (normalizedCountry) {
            return { location: "", country: normalizedCountry };
        }

        const normalizedCity = normalizeLocationValue(city);
        if (!normalizedCity) {
            return { location: "Location unavailable", country: "" };
        }

        const parts = normalizedCity
            .split(",")
            .map((part) => normalizeLocationValue(part))
            .filter(Boolean);

        if (!parts.length) {
            return { location: "Location unavailable", country: "" };
        }

        return { location: parts[parts.length - 1], country: "" };
    };

    const currentSharedPriorities = displayMatch
        ? prioritySummaries[displayMatch.id] || []
        : [];
    const currentMatchInterestsSource = displayMatch?.interests;
    const currentMatchInterests = Array.isArray(currentMatchInterestsSource)
        ? currentMatchInterestsSource.filter((interest): interest is string => Boolean(interest))
        : [];
    const locationInfo = getDisplayLocation(displayMatch?.city, displayMatch?.country);
    const resumeText = [
        displayMatch?.age,
        displayMatch?.gender,
        displayMatch?.sexual_orientation,
        formatPublicProfileDistanceText(displayMatch?.distance, {
            country: user?.country,
            city: user?.city,
        }),
    ]
        .filter(Boolean)
        .join(", ");

    return (
        <>
            <Row crossAxisAlignment="center" className="relative flex-grow min-h-0 overflow-y-hidden md:h-[90%] max-md:gap-5 max-md:max-h-[91svh]">
                <>
                    <Column
                        mainAxisAlignment="start"
                        crossAxisAlignment="center"
                        className="relative gap-5 w-full h-full min-h-0 overflow-hidden scrollbar-hide"
                    >
                        {((reviewingPrevious && previousProfileIndex > 0) ||
                            (!reviewingPrevious && currentUserIndex > 0)) && (
                            <NavigationButton
                                onClick={handlePrevUser}
                                className="max-md:hidden left-2 md:-left-6 lg:left-12 xl:left-12 text-black"
                                ariaLabel="Prev User"
                                isReversed={false}
                                isBackground={false}
                            />
                        )}
                        {((reviewingPrevious && previousProfileIndex < viewedProfiles.length - 1) ||
                            (!reviewingPrevious && matchesSearch && currentUserIndex < matchesSearch.matches.length)) && (
                            <NavigationButton
                                onClick={handleNextUser}
                                className="max-md:hidden right-2 md:-right-6 lg:right-12 xl:right-12 text-black"
                                ariaLabel="Next User"
                                isReversed={true}
                                isBackground={false}
                            />
                        )}
                        <div className="relative flex w-full h-full min-h-0 justify-center">
                            <AnimatePresence
                                initial={false}
                                custom={direction}
                            >
                            {!reviewingPrevious && (searchScopeNeedsReconciliation || !matchesSearch || isCustomComponent || matchesSearch.matches.length === 0) ? (
                                <>
                                    {EndCard(direction)}
                                </>
                            ):(
                                <motion.div
                                    key={`${reviewingPrevious ? "review" : "discovery"}-${displayMatch?.id}`}
                                    custom={direction}
                                    variants={variants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{ duration: 0.4, ease: "easeInOut" }}
                                    onAnimationStart={() => setIsAnimating(true)}
                                    onAnimationComplete={() => setIsAnimating(false)}
                                    className="absolute w-full h-full"
                                >
                                <Column
                                    mainAxisAlignment="center"
                                    crossAxisAlignment="center"
                                    className="flex-shrink-0 w-full h-full min-h-0 overflow-y-hidden"
                                >
                                    <UserView
                                        onTouchStart={handleTouchStart}
                                        onTouchMove={handleTouchMove}
                                        onTouchEnd={handleTouchEnd}
                                        onMouseDown={handleMouseDown}
                                        onMouseMove={handleMouseMove}
                                        onMouseUp={handleMouseUp}
                                        className="select-none cursor-grab md:overflow-y-auto md:min-h-0 max-md:h-[91svh]"
                                        fixedPhotoOnMobile
                                        pictures={sortPhotos(displayMatch?.profile_pictures || [])}
                                        last_logged_in_time={displayMatch?.last_logged_in_time}
                                        show_last_logged={displayMatch?.show_last_logged}
                                        name={displayMatch?.username || ""}
                                        resume={resumeText}
                                        aboutMe={displayMatch?.bio}
                                        location={locationInfo.location}
                                        desires={displayMatch?.desires}
                                        verify={displayMatch?.verify_status === "verified" ? <CheckVerify /> : <CheckVerify color="#c5c5c5" />}
                                        {...(currentMatchInterests.length > 0 && { interest: currentMatchInterests })}
                                        country={locationInfo.country}
						actionOverlay={(
							<Row
                                                mainAxisAlignment="around"
                                                crossAxisAlignment="center"
                                                className="w-full px-8 sm:px-12"
                                            >
                                                <ButtonLikeDislike handleLikeMatch={handleLikeMatch} rippleLike={rippleLike} rippleDislike={rippleDislike} />
                                            </Row>
						)}
                                        detailsClassName="pt-9"
                                        report={reviewingPrevious ? undefined : ({ currentImageUrl }) => (
                                            <button
                                                className="flex h-10 w-10 justify-center items-center bg-white shadow-[0px_2px_8px_rgba(0,0,0,0.1)] rounded-full"
                                                onClick={() => handleIsReport(currentImageUrl)}
                                                >
                                                <Report color="#E50AAC" size={21} />
                                            </button>
                                        )}
                                        partner={displayMatch?.partner}
                                        full
                                        scroll
                                    >
                                        <Column className="gap-2 px-3 pb-3">
                                            <SharedPrioritiesPanel
                                                priorities={currentSharedPriorities}
                                                isToday={displayMatch?.id === todayCandidateId}
                                                onView={() => setMeaningfulOpen(true)}
                                            />
                                            {!reviewingPrevious && displayMatch && (
                                                <PurchaseMessage
                                                    username={displayMatch.username || ""}
                                                    user_id={displayMatch.id || ""}
                                                />
                                            )}
                                        </Column>
                                    </UserView >
                                </Column>
                                </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        {isReport &&
                            <ReportUser
                                isOpenReportUser={isReport}
                                handleCloseReport={handleIsReport}
                                handleSuccessReport={handleSuccessReport}
                                username={displayMatch?.username}
                                id={idReport}
                                reportedImageUrl={reportedImageUrl}
                            />
                        }
                        {meaningfulOpen && displayMatch && currentSharedPriorities.length > 0 &&
                            <MeaningfulIntroductionModal
                                match={displayMatch}
                                priorities={currentSharedPriorities}
                                onClose={() => setMeaningfulOpen(false)}
                                onReport={() => {
                                    setMeaningfulOpen(false)
                                    handleIsReport(displayMatch.profile_pictures?.[0]?.url || "")
                                }}
                            />
                        }
                    </Column >                   
                </>
                <AsideNotification
                    profileImage={profilePictureUrl}
                    username={user?.username || ""}
                />
            </Row >
        </>
    )
}
